// Auto add/remove a free-gift line item based on cart subtotal, with a toast notification.
// Hooks cart mutations by patching window.fetch rather than the theme's internal cart events, which are unreliable here.
(function () {
  if (window.__khcFreeGiftFetchPatched) return;
  window.__khcFreeGiftFetchPatched = true;

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var originalFetch = window.fetch.bind(window);

  window.fetch = function () {
    var args = arguments;
    var input = args[0];
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = originalFetch.apply(window, args);

    if (CART_MUTATION_RE.test(url)) {
      promise
        .then(function () {
          document.dispatchEvent(new CustomEvent('khc:cart-maybe-changed'));
        })
        .catch(function () {});
    }

    return promise;
  };
})();

if (!customElements.get('free-gift-with-purchase')) {
  class FreeGiftWithPurchase extends HTMLElement {
    connectedCallback() {
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.threshold = parseInt(this.dataset.thresholdCents, 10);

      if (!this.variantId || !this.threshold) return;

      this.allowManualRemoval = this.dataset.allowManualRemoval === 'true';
      this.addHeading = this.dataset.toastHeading || '';
      this.addMessage = this.dataset.toastMessage || '';
      this.duration = (parseFloat(this.dataset.toastDuration) || 5) * 1000;
      this.showRemoveToast = this.dataset.showRemoveToast === 'true';
      this.removeHeading = this.dataset.toastRemoveHeading || '';
      this.removeMessage = this.dataset.toastRemoveMessage || '';
      this.undoHeading = this.dataset.toastUndoHeading || '';
      this.undoMessage = this.dataset.toastUndoMessage || '';

      this.toastContainer = this.querySelector('.free-gift-toast-container');

      this._busy = false;
      this._recheckQueued = false;

      this.queueCheck = this.queueCheck.bind(this);

      document.addEventListener('khc:cart-maybe-changed', this.queueCheck);
      window.addEventListener('pageshow', this.queueCheck);

      this.check();
    }

    disconnectedCallback() {
      document.removeEventListener('khc:cart-maybe-changed', this.queueCheck);
      window.removeEventListener('pageshow', this.queueCheck);
    }

    queueCheck() {
      if (this._busy) {
        this._recheckQueued = true;
        return;
      }
      this.check();
    }

    async check() {
      this._busy = true;

      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        const giftLine = cart.items.find((item) => item.variant_id === this.variantId);
        const subtotal = cart.items.reduce((sum, item) => {
          if (item.variant_id === this.variantId) return sum;
          return sum + item.original_line_price;
        }, 0);

        const qualifies = subtotal >= this.threshold;
        const declined = this.allowManualRemoval && cart.attributes && cart.attributes._free_gift_declined === 'true';
        const wasPresent = this._lastGiftLinePresent;
        this._lastGiftLinePresent = !!giftLine;

        if (!qualifies) {
          if (giftLine) await this.changeGiftQuantity(giftLine.key, 0);
          if (declined) await this.setDeclined(false);
        } else if (giftLine) {
          // A free gift should never be stackable.
          if (giftLine.quantity > 1) await this.changeGiftQuantity(giftLine.key, 1);
        } else if (this.allowManualRemoval && wasPresent && !declined) {
          // The line disappeared while the cart still qualifies -- we never remove it
          // ourselves in that state, so this was the customer. Respect it, but let them
          // undo it in case it was a mistake.
          await this.setDeclined(true);
          this.showToast(this.undoHeading, this.undoMessage, {
            actionLabel: 'Undo',
            onAction: () => this.undoDecline(),
          });
        } else if (!declined) {
          await this.addGift();
        }
      } catch (error) {
        // Never break the storefront over a failed background check.
      } finally {
        this._busy = false;
        if (this._recheckQueued) {
          this._recheckQueued = false;
          this.check();
        }
      }
    }

    async addGift() {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
        }),
      });

      if (!response.ok) return;

      await this.refreshUpcart();
      this.showToast(this.addHeading, this.addMessage);
    }

    async changeGiftQuantity(lineKey, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      if (!response.ok) return;

      await this.refreshUpcart();

      if (quantity === 0 && this.showRemoveToast) {
        this.showToast(this.removeHeading, this.removeMessage);
      }
    }

    // Persists "customer removed the gift on purpose" on the cart itself (not local storage) so it
    // survives reloads/devices and is naturally cleared once a fresh cart no longer qualifies.
    async setDeclined(value) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _free_gift_declined: value ? 'true' : '' } }),
      });

      if (!response.ok) return;

      await this.refreshUpcart();
    }

    async undoDecline() {
      await this.setDeclined(false);
      // Re-run the normal check rather than adding directly, in case eligibility
      // changed in the few seconds since the undo prompt appeared.
      this.queueCheck();
    }

    // Our /cart/add.js and /cart/change.js calls bypass UpCart's own add-to-cart flow, so its
    // drawer doesn't know to resync on its own; window.upcartRefreshCart is the app's public API for it.
    async refreshUpcart() {
      if (typeof window.upcartRefreshCart === 'function') {
        try {
          await window.upcartRefreshCart();
        } catch (error) {
          // UpCart isn't installed, or its API changed -- not fatal to the gift logic either way.
        }
      }
    }

    // Nudges the toast left of the UpCart drawer (id="CartPopup") on desktop; the toast's own
    // z-index already keeps it visible above the drawer regardless, this is just cosmetic.
    updateToastPosition() {
      if (!this.toastContainer) return;

      // Below this width the toast is already a full-bleed bar; no room to shift.
      if (window.innerWidth <= 749) {
        this.toastContainer.style.right = '';
        return;
      }

      const drawer = document.getElementById('CartPopup');
      if (drawer) {
        const rect = drawer.getBoundingClientRect();
        const isOnScreen = rect.width > 0 && rect.left < window.innerWidth - 1;
        if (isOnScreen) {
          this.toastContainer.style.right = Math.round(window.innerWidth - rect.left + 20) + 'px';
          return;
        }
      }

      this.toastContainer.style.right = '';
    }

    stopObservingPositionIfIdle() {
      if (this._positionObserver && this.toastContainer && !this.toastContainer.children.length) {
        this._positionObserver.disconnect();
      }
    }

    showToast(heading, message, options) {
      options = options || {};
      if (!this.toastContainer || (!heading && !message)) return;

      this.updateToastPosition();

      if (!this._positionObserver) {
        this._positionObserver = new MutationObserver(() => this.updateToastPosition());
      }
      this._positionObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        subtree: true,
      });

      const toast = document.createElement('div');
      toast.className = 'free-gift-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');

      const icon = document.createElement('span');
      icon.className = 'free-gift-toast__icon';
      const checkTemplate = this.querySelector('template[data-icon="check"]');
      if (checkTemplate) icon.appendChild(checkTemplate.content.cloneNode(true));
      toast.appendChild(icon);

      const body = document.createElement('span');
      body.className = 'free-gift-toast__body';

      if (heading) {
        const headingEl = document.createElement('p');
        headingEl.className = 'free-gift-toast__heading';
        headingEl.textContent = heading;
        body.appendChild(headingEl);
      }

      if (message) {
        const messageEl = document.createElement('p');
        messageEl.className = 'free-gift-toast__message';
        messageEl.textContent = message;
        body.appendChild(messageEl);
      }

      toast.appendChild(body);

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'free-gift-toast__close';
      closeButton.setAttribute('aria-label', 'Dismiss');
      const closeTemplate = this.querySelector('template[data-icon="close"]');
      if (closeTemplate) closeButton.appendChild(closeTemplate.content.cloneNode(true));
      toast.appendChild(closeButton);

      const dismiss = () => {
        toast.classList.remove('is-visible');
        toast.addEventListener(
          'transitionend',
          () => {
            toast.remove();
            this.stopObservingPositionIfIdle();
          },
          { once: true }
        );
        setTimeout(() => {
          toast.remove();
          this.stopObservingPositionIfIdle();
        }, 500);
      };

      toast.querySelector('.free-gift-toast__close').addEventListener('click', dismiss);

      if (options.actionLabel && typeof options.onAction === 'function') {
        const actionButton = document.createElement('button');
        actionButton.type = 'button';
        actionButton.className = 'free-gift-toast__action';
        actionButton.textContent = options.actionLabel;
        actionButton.addEventListener('click', () => {
          dismiss();
          options.onAction();
        });
        body.appendChild(actionButton);
      }

      this.toastContainer.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('is-visible'));

      // An actionable toast (e.g. the undo prompt) stays up until acted on or closed, not auto-dismissed.
      if (!options.actionLabel) {
        setTimeout(dismiss, this.duration);
      }
    }
  }

  customElements.define('free-gift-with-purchase', FreeGiftWithPurchase);
}
